import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-imageoverlay-rotated";
import { renderToStaticMarkup } from "react-dom/server";
import { toast } from "sonner";
import { Siren, Check, Plus, Minus, LocateFixed, ArrowUp, WifiOff, Circle, BatteryWarning, MapPinOff } from "lucide-react";

const FALLBACK_CENTER = [50.0847, 8.2777];

// Selectable base maps (all key-free public tile services).
const BASE_LAYERS = {
  standard: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    opts: { maxZoom: 19, attribution: "© OpenStreetMap" },
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    opts: { maxZoom: 19, attribution: "© Esri, Maxar" },
  },
  topo: {
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    opts: { maxZoom: 17, subdomains: "abc", attribution: "© OpenTopoMap (CC-BY-SA)" },
  },
  light: {
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    opts: { maxZoom: 20, subdomains: "abcd", attribution: "© OpenStreetMap © CARTO" },
  },
};
function makeBaseLayer(key) {
  const cfg = BASE_LAYERS[key] || BASE_LAYERS.standard;
  return L.tileLayer(cfg.url, cfg.opts);
}

const ALARM_SVG = renderToStaticMarkup(
  <Siren size={15} color="#ffffff" strokeWidth={2.5} />
);
const CHECK_SVG = renderToStaticMarkup(
  <Check size={16} color="#ffffff" strokeWidth={3} />
);
const HEADING_SVG = renderToStaticMarkup(
  <ArrowUp size={11} color="#ffffff" strokeWidth={3} />
);
const NOSIGNAL_SVG = renderToStaticMarkup(
  <WifiOff size={10} color="#ffffff" strokeWidth={2.75} />
);
const STATIONARY_SVG = renderToStaticMarkup(
  <Circle size={8} color="#ffffff" fill="#ffffff" strokeWidth={2} />
);
const BATTERY_LOW_SVG = renderToStaticMarkup(
  <BatteryWarning size={11} color="#ffffff" strokeWidth={2.5} />
);
const GHOST_SVG = renderToStaticMarkup(
  <MapPinOff size={10} color="#64748b" strokeWidth={2.5} />
);
const TRAIL_COLOR = "#2563eb";

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

function withIcon(item) {
  return item.alarmState === "active" || item.alarmState === "ack";
}

// Stacking order: active SOS always on top, then the selected chip, then the rest.
// A tracker that is actively moving (has a heading and isn't stationary).
function isMoving(item) {
  return item.sensorType === "tracker" && !item.stationary && item.heading != null;
}

function zFor(item, selected) {
  if (item.alarmState === "active") return 3000;
  if (isMoving(item)) return 2000; // moving Streifen always ride on top of everyone else
  return selected ? 1000 : 0;
}

// The two status lines under the title. An "oldalarm" chip shows only the name
// by default and reveals the alarm time on click; other chips always show their
// lines (last-message time or the active alarm).
function linesFor(item, selected) {
  if (item.alarmState === "oldalarm") {
    return { line1: selected ? item.clickLine || "" : "", line2: "" };
  }
  return { line1: item.line1 || "", line2: item.line2 || "" };
}

function chipHtml(item, selected) {
  const state = item.alarmState;
  const iconState = withIcon(item);
  const iconSvg = state === "ack" ? CHECK_SVG : ALARM_SVG;
  const alarm = iconState ? `<div class="hpin__alarm">${iconSvg}</div>` : "";
  let cls = iconState
    ? ` hpin--${state}`
    : item.stale
    ? " hpin--muted hpin--stale"
    : " hpin--muted";
  if (!iconState && item.noSignal) cls += " hpin--nosignal";
  if (item.ghost) cls += " hpin--ghost";
  const sel = selected ? " is-selected" : "";
  const { line1, line2 } = linesFor(item, selected);
  const isTracker = item.sensorType === "tracker";
  // Movement is shown only while actually moving — a Streife that has become
  // stationary again drops its arrow + coloured border immediately (no timer).
  const heading =
    isTracker && !item.stationary && item.heading != null ? Math.round(item.heading) : null;
  const moveColor = item.moveColor || "#2563eb";
  const headingEl =
    heading != null
      ? `<span class="hpin__heading" title="in Bewegung" style="transform:rotate(${heading}deg);background:${moveColor}">${HEADING_SVG}</span>`
      : "";
  // Rendered for every tracker but only shown (via CSS) when .hpin--nosignal is set.
  const nosignalEl = isTracker
    ? `<span class="hpin__nosignal" title="Möglicherweise Funkloch – kein Empfang">${NOSIGNAL_SVG}</span>`
    : "";
  // Stationary badge (neutral dot). Hidden via CSS when the chip is "kein Signal".
  const stationaryEl =
    isTracker && item.stationary && item.alarmState === "none"
      ? `<span class="hpin__stationary" title="stationär">${STATIONARY_SVG}</span>`
      : "";
  // Low-battery badge (red pill with %) — shown on the chip when a Streife's
  // battery drops under 20 %, regardless of its movement/signal state.
  const lowBatt = isTracker && item.battery != null && item.battery < 20;
  const battEl = lowBatt
    ? `<span class="hpin__lowbatt" title="Schwacher Akku: ${item.battery}%">${BATTERY_LOW_SVG}<span>${item.battery}%</span></span>`
    : "";
  const bodyStyle = item.moveColor && !item.stationary
    ? ` style="border-color:${item.moveColor};border-width:2.5px"`
    : "";
  const ghostEl = item.ghost
    ? `<span class="hpin__ghosticon" title="zuletzt bekannte Position">${GHOST_SVG}</span>`
    : "";
  return (
    `<div class="hpin${cls}${sel}">${alarm}<div class="hpin__arrow"></div>` +
    `<div class="hpin__body"${bodyStyle}>` +
    `<span class="hpin__title">${ghostEl}${headingEl}${stationaryEl}${nosignalEl}${battEl}${escapeHtml(item.title || "Ohne Titel")}</span>` +
    `<span class="hpin__line1" data-role="line1">${escapeHtml(line1)}</span>` +
    `<span class="hpin__line2" data-role="line2">${escapeHtml(line2)}</span>` +
    `</div></div>`
  );
}

function iconFor(item, selected) {
  return L.divIcon({
    html: chipHtml(item, selected),
    className: "hpin-wrap",
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

export default function HomeMap({ items, markerSig, timeSig, trailSig, fitSig, userPos, onNoLocation, overlay, baseLayer, mapApiRef, focusReq, disableAutoFit, guideLine, rotateMs, pickMode, onMapPick }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const trailLayerRef = useRef(null);
  const userLayerRef = useRef(null);
  const guideLayerRef = useRef(null);
  const overlayRef = useRef(null);
  const overlayIdRef = useRef(null);
  const baseLayerRef = useRef(null);
  const markersRef = useRef(new Map());
  const itemsRef = useRef(items);
  const fittedRef = useRef(false);
  const selectedRef = useRef(null);
  const flashRef = useRef(null); // { permid, until } — temporary focus highlight
  const flashTimer = useRef(null);
  const rotationRef = useRef(0);
  const overlapFrontRef = useRef(new Set());
  const rotateMsRef = useRef(rotateMs);
  const userPosRef = useRef(userPos);
  const viewChangeCbRef = useRef(null);
  const pickModeRef = useRef(pickMode);
  const onMapPickRef = useRef(onMapPick);
  itemsRef.current = items;
  userPosRef.current = userPos;
  rotateMsRef.current = rotateMs;
  pickModeRef.current = pickMode;
  onMapPickRef.current = onMapPick;

  useEffect(() => {
    const map = L.map(elRef.current, {
      center: FALLBACK_CENTER,
      zoom: 12,
      zoomControl: false,
      attributionControl: false,
    });
    L.control.attribution({ position: "bottomleft", prefix: false }).addTo(map);
    map.createPane("overlayImagePane");
    map.getPane("overlayImagePane").style.zIndex = 250;
    trailLayerRef.current = L.layerGroup().addTo(map);
    guideLayerRef.current = L.layerGroup().addTo(map);
    markerLayerRef.current = L.layerGroup().addTo(map);
    userLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    if (mapApiRef) {
      mapApiRef.current = {
        getCenter: () => {
          const c = map.getCenter();
          return { lat: c.lat, lon: c.lng };
        },
        getViewWidthMeters: () => {
          const b = map.getBounds();
          return map.distance(b.getNorthWest(), b.getNorthEast());
        },
        fitToCorners: (c) => {
          if (!c || !c.tl || !c.tr || !c.bl) return;
          const tl = L.latLng(c.tl);
          const tr = L.latLng(c.tr);
          const bl = L.latLng(c.bl);
          const br = L.latLng(tr.lat + (bl.lat - tl.lat), tr.lng + (bl.lng - tl.lng));
          map.fitBounds(L.latLngBounds([tl, tr, bl, br]).pad(0.12));
        },
        fitToPoints: (pts) => {
          const valid = (pts || []).filter((p) => p && p[0] != null && p[1] != null);
          if (valid.length === 0) return;
          if (valid.length === 1) {
            map.setView(valid[0], Math.max(map.getZoom(), 16), { animate: true });
          } else {
            map.fitBounds(L.latLngBounds(valid).pad(0.3), { maxZoom: 17, animate: true });
          }
        },
        project: (lat, lon) => {
          if (lat == null || lon == null) return null;
          const p = map.latLngToContainerPoint([lat, lon]);
          return { x: p.x, y: p.y };
        },
        getContainerSize: () => {
          const s = map.getSize();
          return { w: s.x, h: s.y };
        },
        onViewChange: (cb) => {
          viewChangeCbRef.current = cb;
        },
      };
    }
    map.on("move zoom resize", () => {
      if (viewChangeCbRef.current) viewChangeCbRef.current();
    });
    map.on("click", (e) => {
      if (pickModeRef.current && onMapPickRef.current) {
        onMapPickRef.current(e.latlng.lat, e.latlng.lng);
      }
    });
    setTimeout(() => map.invalidateSize(), 200);
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swap the base map (Standard / Satellit / Topo / Hell) and keep it at the back.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (baseLayerRef.current) baseLayerRef.current.remove();
    baseLayerRef.current = makeBaseLayer(baseLayer).addTo(map);
    baseLayerRef.current.bringToBack();
  }, [baseLayer]);

  // Crosshair cursor while in "tap the map to set my location" mode.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getContainer().style.cursor = pickMode ? "crosshair" : "";
  }, [pickMode]);

  // PDF/image overlay (rotatable). Recreated on id/image change, otherwise
  // repositioned/opacity-adjusted in place.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!overlay || !overlay.visible || !overlay.corners) {
      if (overlayRef.current) overlayRef.current.remove();
      if (!overlay) {
        overlayRef.current = null;
        overlayIdRef.current = null;
      }
      return;
    }
    const { id, imageUrl, corners, opacity } = overlay;
    const { tl, tr, bl } = corners;
    if (!tl || !tr || !bl) return;
    const op = opacity == null ? 0.7 : opacity;
    if (!overlayRef.current || overlayIdRef.current !== id) {
      if (overlayRef.current) overlayRef.current.remove();
      overlayRef.current = L.imageOverlay.rotated(
        imageUrl,
        L.latLng(tl),
        L.latLng(tr),
        L.latLng(bl),
        { opacity: op, interactive: false, pane: "overlayImagePane" }
      );
      overlayIdRef.current = id;
      overlayRef.current.addTo(map);
    } else {
      overlayRef.current.reposition(L.latLng(tl), L.latLng(tr), L.latLng(bl));
      overlayRef.current.setOpacity(op);
      if (!map.hasLayer(overlayRef.current)) overlayRef.current.addTo(map);
    }
  }, [overlay]);

  const setLines = (el, item, selected) => {
    const { line1, line2 } = linesFor(item, selected);
    const s1 = el.querySelector('[data-role="line1"]');
    const s2 = el.querySelector('[data-role="line2"]');
    if (s1) s1.textContent = line1;
    if (s2) s2.textContent = line2;
    const chip = el.querySelector(".hpin");
    if (chip) {
      const idle = item.alarmState === "none";
      chip.classList.toggle("hpin--stale", !!item.stale && idle);
      chip.classList.toggle("hpin--nosignal", !!item.noSignal && idle);
    }
  };

  const applySelection = (permid) => {
    const m = markersRef.current.get(permid);
    if (!m) return;
    const selected = selectedRef.current === permid;
    const item = (itemsRef.current || []).find((i) => i.permid === permid) || {};
    m.setZIndexOffset(zFor(item, selected));
    const el = m.getElement && m.getElement();
    if (!el) return;
    const chip = el.querySelector(".hpin");
    if (chip) chip.classList.toggle("is-selected", selected);
    setLines(el, item, selected);
  };

  const selectMarker = (permid) => {
    const prev = selectedRef.current;
    selectedRef.current = prev === permid ? null : permid;
    if (prev) applySelection(prev);
    if (selectedRef.current) applySelection(selectedRef.current);
  };

  // Temporary "look at me" highlight (blink + slight enlarge) on a chip, e.g.
  // when its position icon is tapped in the sidebar. Survives marker rebuilds
  // within the flash window (reapplied in the build effect below).
  const FLASH_MS = 3000;
  const applyFlash = (permid) => {
    const m = markersRef.current.get(permid);
    const el = m && m.getElement && m.getElement();
    const chip = el && el.querySelector(".hpin");
    if (!chip) return;
    chip.classList.remove("hpin--flash");
    void chip.offsetWidth; // force reflow to restart the animation
    chip.classList.add("hpin--flash");
  };
  const flashMarker = (permid) => {
    if (!permid) return;
    flashRef.current = { permid, until: Date.now() + FLASH_MS };
    applyFlash(permid);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => {
      const m = markersRef.current.get(permid);
      const el = m && m.getElement && m.getElement();
      const chip = el && el.querySelector(".hpin");
      if (chip) chip.classList.remove("hpin--flash");
      if (flashRef.current && flashRef.current.permid === permid) flashRef.current = null;
    }, FLASH_MS);
  };

  // Restack markers: active SOS on top, then the selected chip, then the current
  // "front" chip of each overlapping group (rotation), then the rest.
  const restack = () => {
    const front = overlapFrontRef.current;
    const list = itemsRef.current || [];
    markersRef.current.forEach((m, permid) => {
      const item = list.find((i) => i.permid === permid) || {};
      if (item.alarmState === "active") m.setZIndexOffset(3000);
      else if (isMoving(item)) m.setZIndexOffset(2000);
      else if (selectedRef.current === permid) m.setZIndexOffset(1000);
      else if (front.has(permid)) m.setZIndexOffset(600);
      else m.setZIndexOffset(0);
    });
  };

  // Group markers whose on-screen chip boxes overlap; within each group pick one
  // "front" member (advances via rotationRef on each tick). 0 = rotation off.
  const recomputeOverlaps = () => {
    if (!rotateMsRef.current) {
      overlapFrontRef.current = new Set();
      restack();
      return;
    }
    const entries = [];
    const list = itemsRef.current || [];
    markersRef.current.forEach((m, permid) => {
      const el = m.getElement && m.getElement();
      if (!el) return;
      const item = list.find((i) => i.permid === permid) || {};
      if (isMoving(item)) return; // moving chips stay on top → excluded from the rotation
      const chip = el.querySelector(".hpin") || el;
      const r = chip.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return;
      entries.push({ permid, r });
    });
    const parent = {};
    const find = (x) => {
      while (parent[x] !== x) {
        parent[x] = parent[parent[x]];
        x = parent[x];
      }
      return x;
    };
    entries.forEach((e) => {
      parent[e.permid] = e.permid;
    });
    const hit = (a, b) =>
      !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        if (hit(entries[i].r, entries[j].r)) {
          parent[find(entries[i].permid)] = find(entries[j].permid);
        }
      }
    }
    const groups = {};
    entries.forEach((e) => {
      const root = find(e.permid);
      (groups[root] = groups[root] || []).push(e.permid);
    });
    const front = new Set();
    Object.values(groups).forEach((members) => {
      if (members.length < 2) return;
      members.sort();
      front.add(members[rotationRef.current % members.length]);
    });
    overlapFrontRef.current = front;
    restack();
  };

  // (Re)build chip markers when positions / alarm state change.
  useEffect(() => {
    const layer = markerLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    markersRef.current.clear();
    (itemsRef.current || []).forEach((it) => {
      if (it.latitude == null || it.longitude == null) return;
      const selected = selectedRef.current === it.permid;
      const m = L.marker([it.latitude, it.longitude], {
        icon: iconFor(it, selected),
        interactive: true,
        keyboard: false,
        zIndexOffset: zFor(it, selected),
      });
      m.on("click", () => selectMarker(it.permid));
      layer.addLayer(m);
      markersRef.current.set(it.permid, m);
      // keep the focus highlight alive across rebuilds while it's active
      if (flashRef.current && flashRef.current.permid === it.permid && Date.now() < flashRef.current.until) {
        const el = m.getElement && m.getElement();
        const chip = el && el.querySelector(".hpin");
        if (chip) chip.classList.add("hpin--flash");
      }
    });
    if (selectedRef.current && !markersRef.current.has(selectedRef.current)) {
      selectedRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerSig]);

  // Foreground rotation of overlapping chips (configurable interval; 0 = off).
  useEffect(() => {
    recomputeOverlaps();
    if (!rotateMs) return;
    const id = setInterval(() => {
      rotationRef.current += 1;
      recomputeOverlaps();
    }, rotateMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotateMs]);

  // Recompute overlap groups whenever the marker set/positions change.
  useEffect(() => {
    const raf = requestAnimationFrame(() => recomputeOverlaps());
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerSig]);

  // Recompute on pan/zoom (screen-space overlaps change).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const h = () => recomputeOverlaps();
    map.on("moveend zoomend", h);
    return () => map.off("moveend zoomend", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live-tick the status lines (relative "vor X Minuten" text).
  useEffect(() => {
    (itemsRef.current || []).forEach((it) => {
      const m = markersRef.current.get(it.permid);
      const el = m && m.getElement && m.getElement();
      if (el) setLines(el, it, selectedRef.current === it.permid);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSig]);

  // Fading movement trail (last GPS coordinates) per object.
  useEffect(() => {
    const layer = trailLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    (itemsRef.current || []).forEach((it) => {
      if (it.stale || it.stationary) return; // hide trail: stale (>10 min) or parked (<50 m)
      const tr = it.trail || [];
      if (tr.length < 2) return;
      for (let k = 0; k < tr.length - 1; k++) {
        const t = k / (tr.length - 1); // 0 = newest, ~1 = oldest
        L.polyline([tr[k], tr[k + 1]], {
          color: it.moveColor || TRAIL_COLOR,
          weight: 6 * (1 - t) + 1,
          opacity: 0.85 * (1 - t) + 0.08,
          lineCap: "round",
          lineJoin: "round",
          interactive: false,
        }).addTo(layer);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trailSig]);

  // Fit the map to the visible objects when the shown set changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || disableAutoFit) return;
    const pts = (itemsRef.current || [])
      .filter((i) => i.latitude != null && i.longitude != null)
      .map((i) => [i.latitude, i.longitude]);
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.setView(pts[0], Math.max(map.getZoom(), 17), { animate: fittedRef.current });
    } else {
      map.fitBounds(L.latLngBounds(pts).pad(0.08), { maxZoom: 18, animate: fittedRef.current });
    }
    fittedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitSig, disableAutoFit]);

  // Pan/zoom to a requested object (list click) and highlight it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusReq) return;
    const { lat, lon, permid } = focusReq;
    if (lat != null && lon != null) {
      map.setView([lat, lon], Math.max(map.getZoom(), 17), { animate: true });
    }
    if (permid) {
      const prev = selectedRef.current;
      selectedRef.current = permid;
      if (prev && prev !== permid) applySelection(prev);
      applySelection(permid);
      flashMarker(permid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusReq]);

  // Dashed guide line from the user to the target rescue point (alarm deep-link).
  useEffect(() => {
    const layer = guideLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!guideLine || !guideLine.from || !guideLine.to) return;
    L.polyline([guideLine.from, guideLine.to], {
      color: "#dc2626",
      weight: 3,
      opacity: 0.8,
      dashArray: "8 8",
      lineCap: "round",
      interactive: false,
    }).addTo(layer);
  }, [guideLine]);

  // Blue "my location" dot (Google-Maps style, no direction) + accuracy circle.
  useEffect(() => {
    const layer = userLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!userPos) return;
    const { lat, lon, accuracy } = userPos;
    if (accuracy && accuracy <= 2000) {
      L.circle([lat, lon], {
        radius: accuracy,
        color: "#1a73e8",
        weight: 1,
        opacity: 0.3,
        fillColor: "#1a73e8",
        fillOpacity: 0.12,
        interactive: false,
      }).addTo(layer);
    }
    L.marker([lat, lon], {
      icon: L.divIcon({
        html: '<div class="user-dot"></div>',
        className: "user-dot-wrap",
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      }),
      interactive: false,
      keyboard: false,
      zIndexOffset: 2000,
    }).addTo(layer);
  }, [userPos]);

  const zoom = (delta) => {
    const map = mapRef.current;
    if (map) map.setZoom(map.getZoom() + delta);
  };

  const focusUser = () => {
    const map = mapRef.current;
    const up = userPosRef.current;
    if (!up) {
      if (onNoLocation) onNoLocation();
      else toast.error("Kein Standort verfügbar – bitte Standortfreigabe aktivieren.");
      return;
    }
    if (map) map.setView([up.lat, up.lon], Math.max(map.getZoom(), 16));
  };

  return (
    <div className="relative h-full w-full" data-testid="home-map">
      <div ref={elRef} className="absolute inset-0 z-0" />
      <div className="absolute bottom-4 right-3 z-[500] flex flex-col gap-2">
        <button
          onClick={focusUser}
          data-testid="home-focus-button"
          aria-label="Auf meinen Standort zentrieren"
          className="mb-1 grid h-11 w-11 place-items-center rounded-2xl bg-white text-blue-600 shadow-lg ring-1 ring-slate-200 transition-transform hover:scale-[1.05] active:scale-[0.95]"
        >
          <LocateFixed size={20} />
        </button>
        <button
          onClick={() => zoom(1)}
          data-testid="home-zoom-in"
          aria-label="Vergrößern"
          className="grid h-11 w-11 place-items-center rounded-2xl bg-white text-slate-700 shadow-lg ring-1 ring-slate-200 transition-transform hover:scale-[1.05] active:scale-[0.95]"
        >
          <Plus size={20} />
        </button>
        <button
          onClick={() => zoom(-1)}
          data-testid="home-zoom-out"
          aria-label="Verkleinern"
          className="grid h-11 w-11 place-items-center rounded-2xl bg-white text-slate-700 shadow-lg ring-1 ring-slate-200 transition-transform hover:scale-[1.05] active:scale-[0.95]"
        >
          <Minus size={20} />
        </button>
      </div>
    </div>
  );
}
